/**
 * Vanilla modal module — replaces Bootstrap 3 modal JS.
 *
 * Trigger markup (new):
 *   <button data-modal-toggle data-target="#myModal">Open</button>
 *   <button data-modal-dismiss>Close</button>
 *
 * Dismiss: `data-modal-dismiss` attribute on close buttons.
 *
 * CSS contract (unchanged from Bootstrap 3):
 *   .modal          { display: none; }
 *   .modal.in       { display: block; }  (we set display explicitly)
 *   .modal-backdrop.in { opacity: 0.5; }
 *   .modal-open     { overflow: hidden; } (on body)
 *   .modal.fade .modal-dialog  { transform: translate(0, -25%); }
 *   .modal.in .modal-dialog    { transform: translate(0, 0); }
 *
 * Exposes window.__modal for programmatic use:
 *   window.__modal.show(el | "#id")
 *   window.__modal.hide(el | "#id")
 *   window.__modal.toggle(el | "#id")
 *
 * Monkey-patches $.fn.modal so existing jQuery calls and data-confirm-modal
 * package keep working without modification.
 *
 * Emits jQuery events on the modal element for backward compatibility:
 *   show.bs.modal, shown.bs.modal, hide.bs.modal, hidden.bs.modal
 */

// Guard against double-initialisation when multiple packs import this module
if ((window as any).__modalInitialised) {
  // already running — bail out so listeners aren't registered twice
} else {
(window as any).__modalInitialised = true;

const TOGGLE_ATTR = "data-modal-toggle";
const DISMISS_ATTR = "data-modal-dismiss";
const FADE_DURATION = 300;

let openModals: HTMLElement[] = [];
let backdropEl: HTMLElement | null = null;
const pendingHideTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveModal(target: HTMLElement | string): HTMLElement | null {
  const el = typeof target === "string"
    ? document.querySelector<HTMLElement>(target)
    : target;
  return el?.classList?.contains("modal") ? el : null;
}

function resolveFromTrigger(trigger: HTMLElement): HTMLElement | null {
  const selector = trigger.getAttribute("data-target") || trigger.getAttribute("href");
  if (selector && selector.startsWith("#")) return document.querySelector<HTMLElement>(selector);
  return null;
}

function emitEvent(modal: HTMLElement, name: string): void {
  // Dispatch native CustomEvent
  modal.dispatchEvent(new CustomEvent(name, { bubbles: true }));

  // Also trigger jQuery event if jQuery is available (for existing .on() listeners)
  const $ = (window as any).jQuery;
  if ($) {
    $(modal).trigger(name);
  }
}

// ---------------------------------------------------------------------------
// Backdrop
// ---------------------------------------------------------------------------

function showBackdrop(): void {
  // If our tracked backdrop got detached from the DOM (e.g. some other code
  // path called `.remove()` on it, or replaced body's children), drop the
  // stale reference so we recreate cleanly instead of holding a ghost.
  if (backdropEl && !backdropEl.isConnected) backdropEl = null;
  if (backdropEl) return;
  // Defensive: remove any backdrop elements left over from earlier flows
  // before we create the new one, so we never end up with two on the page.
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
  backdropEl = document.createElement("div");
  backdropEl.className = "modal-backdrop fade";
  document.body.appendChild(backdropEl);
  // Force reflow before adding .in for CSS transition
  void backdropEl.offsetHeight;
  backdropEl.classList.add("in");
}

function hideBackdrop(): void {
  if (!backdropEl) {
    // No tracked backdrop, but a stale `.modal-backdrop` may still be in
    // the DOM (e.g. created by a flow that lost the JS reference). Sweep
    // any orphans so the scrim doesn't stay on the page.
    document.querySelectorAll(".modal-backdrop").forEach((b) => {
      b.classList.remove("in");
      setTimeout(() => b.remove(), FADE_DURATION);
    });
    return;
  }
  backdropEl.classList.remove("in");
  const el = backdropEl;
  backdropEl = null;
  setTimeout(() => el.remove(), FADE_DURATION);
}

// ---------------------------------------------------------------------------
// Show / Hide
// ---------------------------------------------------------------------------

function show(target: HTMLElement | string): void {
  const modal = resolveModal(target);
  if (!modal) return;

  // Cancel any in-flight hide animation for this modal
  const pendingTimer = pendingHideTimers.get(modal);
  if (pendingTimer !== undefined) {
    clearTimeout(pendingTimer);
    pendingHideTimers.delete(modal);
    modal.style.display = "block";
    modal.setAttribute("aria-hidden", "false");
  }

  if (modal.classList.contains("in")) return; // already visible

  emitEvent(modal, "show.bs.modal");

  document.body.classList.add("modal-open");
  showBackdrop();

  modal.style.display = "block";
  modal.setAttribute("aria-hidden", "false");

  // Force reflow before adding .in for CSS transition
  void modal.offsetHeight;
  modal.classList.add("in");

  if (!openModals.includes(modal)) {
    openModals.push(modal);
  }

  setTimeout(() => {
    emitEvent(modal, "shown.bs.modal");
  }, FADE_DURATION);
}

function hide(target: HTMLElement | string): void {
  const modal = resolveModal(target);
  if (!modal) return;
  if (!modal.classList.contains("in")) return; // already hidden

  emitEvent(modal, "hide.bs.modal");

  modal.classList.remove("in");

  const timer = setTimeout(() => {
    pendingHideTimers.delete(modal);
    modal.style.display = "none";
    modal.setAttribute("aria-hidden", "true");

    // Drop the modal we just hid, plus any stale entries: tracked modals that
    // are neither visible (no `in` class) nor in the middle of a hide animation
    // (no pending timer). Without this prune, an unrelated `openModals` leak
    // anywhere on the page leaves `openModals.length > 0`, which makes us skip
    // `hideBackdrop()` and strands the scrim on screen.
    openModals = openModals.filter((m) => {
      if (m === modal) return false;
      if (m.classList.contains("in")) return true;
      if (pendingHideTimers.has(m)) return true;
      return false;
    });

    if (openModals.length === 0) {
      document.body.classList.remove("modal-open");
      hideBackdrop();
    }

    emitEvent(modal, "hidden.bs.modal");
  }, FADE_DURATION);
  pendingHideTimers.set(modal, timer);
}

function toggle(target: HTMLElement | string): void {
  const modal = resolveModal(target);
  if (!modal) return;
  if (modal.classList.contains("in")) {
    hide(modal);
  } else {
    show(modal);
  }
}

// ---------------------------------------------------------------------------
// Event listeners (delegated)
// ---------------------------------------------------------------------------

// Open trigger: data-modal-toggle with data-target or href
document.addEventListener("click", (e: MouseEvent) => {
  const trigger = (e.target as HTMLElement).closest<HTMLElement>(`[${TOGGLE_ATTR}]`);
  if (trigger) {
    e.preventDefault();
    const modal = resolveFromTrigger(trigger);
    if (modal) show(modal);
    return;
  }

  // Dismiss: data-modal-dismiss attribute
  const dismiss =
    (e.target as HTMLElement).closest<HTMLElement>(`[${DISMISS_ATTR}]`);
  if (dismiss) {
    const modal = dismiss.closest<HTMLElement>(".modal");
    if (modal) {
      // Only preventDefault for <a> tags to avoid hash jumps.
      // Do NOT preventDefault on other elements — link_to wrapping a
      // dismiss div (e.g. _modal.html.erb confirm button) relies on
      // event propagation to trigger Rails UJS AJAX requests.
      if (dismiss.tagName === "A") e.preventDefault();
      hide(modal);
    }
    return;
  }

  // Click on backdrop (the .modal element itself, outside .modal-dialog)
  const target = e.target as HTMLElement;
  if (target.classList.contains("modal") && target.classList.contains("in")) {
    hide(target);
  }
});

// Escape key closes topmost modal
document.addEventListener("keydown", (e: KeyboardEvent) => {
  if (e.key === "Escape" && openModals.length > 0) {
    hide(openModals[openModals.length - 1]);
  }
});

// ---------------------------------------------------------------------------
// Programmatic API
// ---------------------------------------------------------------------------

interface ModalAPI {
  show(target: HTMLElement | string): void;
  hide(target: HTMLElement | string): void;
  toggle(target: HTMLElement | string): void;
}

const modalAPI: ModalAPI = { show, hide, toggle };
(window as any).__modal = modalAPI;

// ---------------------------------------------------------------------------
// jQuery $.fn.modal monkey-patch
// ---------------------------------------------------------------------------
// This keeps data-confirm-modal and all existing $().modal() calls working.

function patchJQueryModal(): void {
  const $ = (window as any).jQuery;
  if (!$) return;

  $.fn.modal = function (action?: string | Record<string, unknown>) {
    return this.each(function (this: HTMLElement) {
      if (typeof action === "string") {
        switch (action) {
          case "show":
            show(this);
            break;
          case "hide":
            hide(this);
            break;
          case "toggle":
            toggle(this);
            break;
        }
      } else if (action && typeof action === "object" && action.show === false) {
        // no-op: initialise without showing (Bootstrap 3 compat)
      } else {
        show(this);
      }
    });
  };

  // data-confirm-modal only checks existence; not a full Bootstrap 3 Constructor
  $.fn.modal.Constructor = { VERSION: "3.4.1" };
}

// Patch immediately if jQuery is available, otherwise wait
if ((window as any).jQuery) {
  patchJQueryModal();
} else {
  document.addEventListener("DOMContentLoaded", patchJQueryModal);
}

} // end of double-init guard

